import CheckCircleOutline from '@mui/icons-material/CheckCircleOutline';
import LunchDining from '@mui/icons-material/LunchDining';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import { Box, ButtonBase, CircularProgress, Divider, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { keyframes } from '@mui/system';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useOperatorLivePresence } from '../contexts/OperatorLivePresenceContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useSipExtension } from '../hooks/useSipExtension';
import {
  setMyTelephonyAvailabilityAvailable,
  setMyTelephonyAvailabilityUnavailable,
  type MyTelephonyUnavailabilityReason,
} from '../services/users';
import { isOperatorReadinessBlockedByAvailability } from '../utils/operatorTelephonyAvailability';
import UserTelephonyAvailabilityStatus from './users/UserTelephonyAvailabilityStatus';

const operatorAvailabilityPulse = keyframes`
  0% {
    border-color: var(--operator-pill-border-start);
    box-shadow: 0 0 0 0 var(--operator-pill-shadow-start);
  }
  50% {
    border-color: var(--operator-pill-border-end);
    box-shadow: 0 0 0 4px var(--operator-pill-shadow-end);
  }
  100% {
    border-color: var(--operator-pill-border-start);
    box-shadow: 0 0 0 0 var(--operator-pill-shadow-start);
  }
`;

/**
 * Global header control for the authenticated operator telephony availability.
 *
 * The pill is intentionally self-service only in V1 and only renders for users
 * with a telephony-eligible endpoint.
 */
export default function OperatorAvailabilityPill() {
  const { t } = useTranslation(['common']);
  const theme = useTheme();
  const { user, updateUser } = useAuth();
  const { readinessState, isConfirmingReadiness, confirmReadyForTransfers } = useOperatorLivePresence();
  const { hasPermission, isLoading: permissionsLoading } = usePermissions();
  const { connectionState } = useSipExtension();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [submitting, setSubmitting] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nowMs, setNowMs] = useState(() => Date.now());

  const availability = user?.telephonyAvailability ?? null;
  const isEligible = Boolean(availability?.isEligible);
  const isTenantEligible = availability?.isTenantEligible ?? true;
  const canLaunchSip = hasPermission(['calls_create', 'calls_manual_launch_sip'], true);
  const isVisible = Boolean(isEligible && isTenantEligible && canLaunchSip);
  const isUnavailable = Boolean(isVisible && !availability?.isAvailable);
  const isMenuOpen = Boolean(anchorEl);
  const isReadinessBlocked = isOperatorReadinessBlockedByAvailability(availability, readinessState);

  useEffect(() => {
    if (!isUnavailable) {
      return undefined;
    }
    const intervalId = window.setInterval(() => setNowMs(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [isUnavailable]);

  const menuOptions = useMemo(() => {
    if (!availability?.isEligible) {
      return [];
    }
    if (availability.isAvailable) {
      return [
        {
          key: 'break',
          label: t('common:operatorAvailability.menu.setBreak'),
          icon: <PauseCircleFilled fontSize="small" />,
        },
        {
          key: 'lunch',
          label: t('common:operatorAvailability.menu.setLunch'),
          icon: <LunchDining fontSize="small" />,
        },
      ];
    }

    if (isReadinessBlocked) {
      return [
        {
          key: 'confirm_ready',
          label: t('common:operatorAvailability.menu.confirmReady'),
          icon: <CheckCircleOutline fontSize="small" />,
        },
        {
          key: 'break',
          label: t('common:operatorAvailability.menu.setBreak'),
          icon: <PauseCircleFilled fontSize="small" />,
        },
        {
          key: 'lunch',
          label: t('common:operatorAvailability.menu.setLunch'),
          icon: <LunchDining fontSize="small" />,
        },
      ];
    }

    const nextOptions = [
      {
        key: 'available',
        label: t('common:operatorAvailability.menu.setAvailable'),
        icon: <CheckCircleOutline fontSize="small" />,
      },
    ];

    if (availability.reason !== 'break') {
      nextOptions.push({
        key: 'break',
        label: t('common:operatorAvailability.menu.setBreak'),
        icon: <PauseCircleFilled fontSize="small" />,
      });
    }

    if (availability.reason !== 'lunch') {
      nextOptions.push({
        key: 'lunch',
        label: t('common:operatorAvailability.menu.setLunch'),
        icon: <LunchDining fontSize="small" />,
      });
    }

    return nextOptions;
  }, [availability, isReadinessBlocked, t]);

  const connectionBadge = useMemo(() => {
    switch (connectionState) {
      case 'connected':
        return {
          label: t('common:operatorAvailability.connection.connected'),
        };
      case 'checking':
        return {
          label: t('common:operatorAvailability.connection.checking'),
        };
      case 'recovering':
        return {
          label: t('common:operatorAvailability.connection.recovering'),
        };
      case 'disconnected':
        return {
          label: t('common:operatorAvailability.connection.disconnected'),
        };
      case 'blocked_auth':
        return {
          label: t('common:operatorAvailability.connection.blockedAuth'),
        };
      case 'error':
        return {
          label: t('common:operatorAvailability.connection.error'),
        };
      default:
        return null;
    }
  }, [connectionState, t]);

  const transientConnectionStyles = useMemo(() => {
    if (connectionState === 'checking') {
      return {
        '--operator-pill-border-start': alpha(theme.palette.info.main, 0.36),
        '--operator-pill-border-end': alpha(theme.palette.info.light, 0.9),
        '--operator-pill-shadow-start': alpha(theme.palette.info.main, 0),
        '--operator-pill-shadow-end': alpha(theme.palette.info.main, 0.18),
        animation: `${operatorAvailabilityPulse} 1.45s ease-in-out infinite`,
      } as const;
    }

    if (connectionState === 'recovering') {
      return {
        '--operator-pill-border-start': alpha(theme.palette.warning.main, 0.4),
        '--operator-pill-border-end': alpha(theme.palette.warning.light, 0.95),
        '--operator-pill-shadow-start': alpha(theme.palette.warning.main, 0),
        '--operator-pill-shadow-end': alpha(theme.palette.warning.main, 0.2),
        animation: `${operatorAvailabilityPulse} 1.6s ease-in-out infinite`,
      } as const;
    }

    return null;
  }, [connectionState, theme]);

  if (permissionsLoading || !isVisible) {
    return null;
  }

  const handleClose = () => {
    if (submitting || isConfirmingReadiness) {
      return;
    }
    setAnchorEl(null);
    setError(null);
  };

  const handleSelect = async (nextState: 'available' | MyTelephonyUnavailabilityReason) => {
    setSubmitting(nextState);
    setError(null);
    try {
      const nextUser = nextState === 'available'
        ? await setMyTelephonyAvailabilityAvailable()
        : await setMyTelephonyAvailabilityUnavailable(nextState);
      updateUser(nextUser);
      if (nextState === 'available') {
        await confirmReadyForTransfers();
      }
      setAnchorEl(null);
    } catch (requestError) {
      console.error('Error updating operator availability:', requestError);
      setError(t('common:operatorAvailability.menu.error'));
    } finally {
      setSubmitting(null);
    }
  };

  const handleConfirmReady = async () => {
    setSubmitting('confirm_ready');
    setError(null);
    try {
      await confirmReadyForTransfers();
      setAnchorEl(null);
    } catch (requestError) {
      console.error('Error confirming operator readiness:', requestError);
      setError(t('common:operatorAvailability.menu.error'));
    } finally {
      setSubmitting(null);
    }
  };

  return (
    <>
      <Tooltip
        title={connectionBadge?.label || ''}
        arrow
        disableInteractive
        placement="bottom"
      >
      <ButtonBase
        onClick={(event) => {
          setAnchorEl(event.currentTarget);
          setError(null);
        }}
        sx={{
          display: { xs: 'none', md: 'inline-flex' },
          alignItems: 'flex-start',
          justifyContent: 'flex-start',
          borderRadius: 0,
          textAlign: 'left',
          minWidth: 0,
        }}
        aria-label={connectionBadge?.label || undefined}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: 0.3 }}>
          <UserTelephonyAvailabilityStatus
            availability={availability}
            nowMs={nowMs}
            showTimer
            showIneligibleState={false}
            direction="column"
            chipSize="small"
            timerColor="rgba(255,255,255,0.82)"
            gap={0.35}
            timerFontSize="0.69rem"
            chipSx={{
              ...(availability?.isAvailable
                ? {
                  padding: '8px !important',
                  height: 'auto',
                }
                : {
                  px: 0.45,
                }),
              ...(transientConnectionStyles || {}),
              '& .MuiChip-label': {
                px: 0.9,
              },
            }}
          />
        </Box>
      </ButtonBase>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleClose}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
      >
        <Box sx={{ px: 2, py: 1.5, maxWidth: 320 }}>
          <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
            {t('common:operatorAvailability.menu.title')}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {t('common:operatorAvailability.menu.description')}
          </Typography>
        </Box>
        <Divider />
        {menuOptions.map((option) => (
          <MenuItem
            key={option.key}
            disabled={Boolean(submitting || isConfirmingReadiness)}
            onClick={() => {
              if (option.key === 'confirm_ready') {
                void handleConfirmReady();
                return;
              }
              const nextState = option.key === 'available'
                ? 'available'
                : (option.key as MyTelephonyUnavailabilityReason);
              void handleSelect(nextState);
            }}
          >
            <ListItemIcon>
              {submitting === option.key ? <CircularProgress size={16} /> : option.icon}
            </ListItemIcon>
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
        {error ? (
          <Box sx={{ px: 2, py: 1 }}>
            <Typography variant="caption" color="error">
              {error}
            </Typography>
          </Box>
        ) : null}
      </Menu>
    </>
  );
}
